import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { FiSave, FiLock, FiUser } from 'react-icons/fi';
import Swal from 'sweetalert2';
import { updatePassword } from '../services/adminService';

const Settings = () => {
  const admin = useSelector((state) => state.admin.admin);
  
  const [activeTab, setActiveTab] = useState('account');
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  const validatePassword = () => {
    const newErrors = {};
    
    if (!passwordData.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }
    
    if (!passwordData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else {
      const minLength = /.{8,32}/;
      const uppercase = /[A-Z]/;
      const lowercase = /[a-z]/;
      const number = /[0-9]/;
      const specialChar = /[@$!%*?&]/;
      const noSpaces = /^\S*$/;
      
      if (!minLength.test(passwordData.newPassword)) {
        newErrors.newPassword = 'Password must be between 8 to 32 characters long';
      } else if (!uppercase.test(passwordData.newPassword)) {
        newErrors.newPassword = 'Password must contain at least one uppercase letter';
      } else if (!lowercase.test(passwordData.newPassword)) {
        newErrors.newPassword = 'Password must contain at least one lowercase letter';
      } else if (!number.test(passwordData.newPassword)) {
        newErrors.newPassword = 'Password must contain at least one number';
      } else if (!specialChar.test(passwordData.newPassword)) {
        newErrors.newPassword = 'Password must contain at least one special character (@, $, !, %, *, ?, &)';
      } else if (!noSpaces.test(passwordData.newPassword)) {
        newErrors.newPassword = 'Password should not contain spaces';
      }
    }
    
    if (!passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePassword()) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await updatePassword(passwordData);
      
      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Password updated successfully',
          confirmButtonColor: '#f97316'
        });
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      }
    } catch (error) {
      const errorMessage = error.message || 'Failed to update password';
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage,
        confirmButtonColor: '#f97316'
      });
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-800">Settings</h1>
      
      <div className="bg-white rounded-lg shadow">
        <div className="flex border-b border-gray-200">
          <button
            className={`py-4 px-6 text-sm font-medium flex items-center ${
              activeTab === 'account'
                ? 'text-orange-500 border-b-2 border-orange-500'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('account')}
          >
            <FiUser className="mr-2" />
            Account
          </button>
          <button
            className={`py-4 px-6 text-sm font-medium flex items-center ${
              activeTab === 'security'
                ? 'text-orange-500 border-b-2 border-orange-500'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('security')}
          >
            <FiLock className="mr-2" />
            Security
          </button>
        </div>
        
        <div className="p-6">
          {activeTab === 'account' && (
            <div>
              <h2 className="text-lg font-medium text-gray-800 mb-4">Account Information</h2>
              <div className="mb-6">
                <div className="flex items-center mb-6">
                  <div className="h-14 w-14 rounded-full bg-orange-500 flex items-center justify-center text-white text-xl font-bold mr-4">
                    {admin?.username?.charAt(0).toUpperCase() || 'A'}
                  </div>
                  <div>
                    <h3 className="text-lg font-medium">{admin?.username || 'Admin User'}</h3>
                    <p className="text-sm text-gray-500">{admin?.email || 'admin@example.com'}</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      value={admin?.username || ''}
                      disabled
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      value={admin?.email || ''}
                      disabled
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      value={admin?.role || 'admin'}
                      disabled
                    />
                  </div>
                </div>
              </div>
              
              <div className="border-t border-gray-200 pt-6">
                <p className="text-sm text-gray-500 mb-4">
                  To change your account details, please contact a super admin.
                </p>
              </div>
            </div>
          )}
          
          {activeTab === 'security' && (
            <div>
              <h2 className="text-lg font-medium text-gray-800 mb-4">Change Password</h2>
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="currentPassword">
                    Current Password
                  </label>
                  <input
                    id="currentPassword"
                    name="currentPassword"
                    type="password"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                      errors.currentPassword ? 'border-red-500' : 'border-gray-300'
                    }`}
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                  {errors.currentPassword && (
                    <p className="mt-1 text-sm text-red-500">{errors.currentPassword}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="newPassword">
                    New Password
                  </label>
                  <input
                    id="newPassword"
                    name="newPassword"
                    type="password"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                      errors.newPassword ? 'border-red-500' : 'border-gray-300'
                    }`}
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                  {errors.newPassword && (
                    <p className="mt-1 text-sm text-red-500">{errors.newPassword}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="confirmPassword">
                    Confirm New Password
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                      errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                    }`}
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-500">{errors.confirmPassword}</p>
                  )}
                </div>
                
                <div className="pt-4">
                  <button
                    type="submit"
                    className="flex items-center px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={loading}
                  >
                    <FiSave className="mr-2" />
                    {loading ? 'Updating...' : 'Update Password'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings; 